// Hideo - the app's own dialogs (in place of the browser's alert / confirm / prompt), the thumbs-up of important moments
// and the short online-save notice at the bottom. Drawn by src/components/notices/NoticeLayer.vue (in App.vue).
// The code imports uiConfirm / uiPrompt / uiAlert / uiSuccess / syncToast from here (and window.alert is uiAlert:
// loadApp, src/features/boot.ts).
//
// Like the legacy dialogs: every call opens one more dialog on top of the others (they are never queued one after the
// other); each one listens to the keyboard on the document in the capture phase from the moment it opens, so Enter / Esc
// answer every dialog on screen at once and no other key reaches what is below; the cursor goes back where it was.
import { defineStore } from 'pinia'
import { nextTick, reactive, ref } from 'vue'
import { t } from '../i18n'
import {
  alertSpec, confirmSpec, dialogKeyAction, dialogResult, dialogSpec, promptSpec, SUCCESS_MS, TOAST_MS,
  type ConfirmOptions, type PromptOptions, type UiDialogOptions, type UiDialogSpec
} from '../features/notices'
import { playSound } from '../features/sounds'

/** A dialog on screen */
export interface UiDialogItem extends UiDialogSpec {
  id: number
  /** What is typed in the text field (a prompt) */
  value: string
}

interface Control {
  resolve: (v: boolean | string | null) => void
  key: (e: KeyboardEvent) => void
  /** The element that had the cursor when the dialog opened */
  prevFocus: Element | null
  /** The dialog's overlay, once drawn (UiDialog.vue) */
  el: HTMLElement | null
}

export const useNoticesStore = defineStore('notices', () => {
  /** App dialogs on screen, the topmost last */
  const dialogs = reactive<UiDialogItem[]>([])
  const ctl = new Map<number, Control>()
  /** Overlays of dialogs just answered, still in the page until the next redraw: the cursor never goes back into them */
  const closing = new Set<HTMLElement>()
  let n = 0

  function open(o: UiDialogOptions | UiDialogSpec): Promise<boolean | string | null> {
    const spec = dialogSpec(o)
    return new Promise(resolve => {
      const id = ++n
      const key = (e: KeyboardEvent) => {
        const a = dialogKeyAction(e.key)
        e.stopPropagation()
        if (a !== 'block') { e.preventDefault(); answer(id, a === 'yes') }
      }
      ctl.set(id, { resolve, key, prevFocus: document.activeElement, el: null })
      document.addEventListener('keydown', key, true)
      dialogs.push({ ...spec, id, value: spec.input ?? '' })
      // The themes' sounds (off by default, src/features/sounds.ts): a message sounds like an error, a question opens
      playSound(spec.cancel ? 'open' : 'error')
    })
  }

  /** The dialog's overlay is on screen (UiDialog.vue) */
  function attach(id: number, el: HTMLElement) {
    const c = ctl.get(id)
    if (c) c.el = el
  }

  /** Answers a dialog: yes = OK (or Enter), no = Cancel (or Esc, a click outside a question). Twice = nothing */
  function answer(id: number, yes: boolean) {
    const c = ctl.get(id), i = dialogs.findIndex(x => x.id === id)
    if (!c || i < 0) return
    ctl.delete(id)
    document.removeEventListener('keydown', c.key, true)
    const [item] = dialogs.splice(i, 1)
    if (c.el) { const el = c.el; closing.add(el); nextTick(() => closing.delete(el)) }
    const prev = c.prevFocus as HTMLElement | null
    try {
      if (prev && prev.focus && document.contains(prev) && ![...closing].some(el => el.contains(prev))) prev.focus({ preventScroll: true })
    } catch { /* an element that cannot take the cursor */ }
    playSound(yes ? (item.cancel ? 'confirm' : 'click') : 'back')
    c.resolve(dialogResult(item, yes, item.value))
  }

  // ===== Thumbs-up (uiSuccess): a new one replaces the one on screen =====
  const ok = ref<{ text: string; seq: number } | null>(null)
  let okSeq = 0
  function success(text?: unknown) {
    const seq = ++okSeq
    ok.value = { text: text === undefined ? t('Done!') : String(text ?? ''), seq }
    playSound('ok')
    setTimeout(() => { if (ok.value && ok.value.seq === seq) ok.value = null }, SUCCESS_MS)
  }

  // ===== Online-save notice at the bottom (syncToast): drawn the first time, then it fades out and comes back =====
  const toast = reactive({ shown: false, on: false, text: '' })
  let toastTimer: ReturnType<typeof setTimeout> | undefined
  function showToast(text: unknown) {
    toast.shown = true
    toast.text = String(text ?? '')
    playSound('notify')
    toast.on = true
    clearTimeout(toastTimer)
    toastTimer = setTimeout(() => { toast.on = false }, TOAST_MS)
  }

  return { dialogs, open, attach, answer, ok, success, toast, showToast }
})

/** An app dialog with any options (uiConfirm, uiPrompt and uiAlert are the usual ones) */
export const uiDialog = (o: UiDialogOptions) => useNoticesStore().open(o)

/** A question: true = OK, false = Cancel (Esc, a click outside). Message and title are plain text: pass raw user data
 *  in t() placeholders, never esc() */
export const uiConfirm = (message: unknown, o: ConfirmOptions = {}) => useNoticesStore().open(confirmSpec(message, o)) as Promise<boolean>
/** "Delete" confirmation of an edit dialog (red Delete button); true when confirmed */
export const confirmDelete = (message: string): Promise<boolean> => uiConfirm(message, { ok: t('Delete'), danger: true })

/** A question with a text field: the text typed, or null = Cancel (Esc, a click outside) */
export const uiPrompt = (message: unknown, value: unknown = '', o: PromptOptions = {}) => useNoticesStore().open(promptSpec(message, value, o)) as Promise<string | null>

/** A message with OK. Non-blocking: the code after it goes on at once (it also replaces window.alert) */
export function uiAlert(message?: unknown): void { void useNoticesStore().open(alertSpec(message)) }
/** A message with OK that tells when it is closed (the cursor is already back where it was): e.g. to put the cursor in
 *  the required field the message is about */
export const uiAlertThen = (message?: unknown): Promise<void> => useNoticesStore().open(alertSpec(message)).then(() => undefined)

/** Animated thumbs-up in the middle of the screen, for important moments only (never for saves: docs/decisions.md) */
export function uiSuccess(text?: unknown): void { useNoticesStore().success(text) }

/** Short notice at the bottom (blocks nothing): online saves, a video moved between the plan and Published… */
export function syncToast(text: unknown): void { useNoticesStore().showToast(text) }
